import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getProductionDeclarationApproveDefaultMessage,
  getProductionDeclarationRefuseDefaultMessage,
} from '@/features/production-declaration/config/production-declaration-send-default-message';
import type { ProductionDeclarationActionCallbacks } from '@/features/production-declaration/hooks/use-production-declaration-action';
import type { ProductionDeclarationEmailAction } from '@/features/production-declaration/production-declaration-send-email-dialog';
import type { SupplierContact } from '@/types/core/purchase-planning/supplier';

type UseProductionDeclarationEmailDialogParams = {
  productionDeclarationId: string;
  purchaseOrderId: number;
  purchaseOrderName?: string | null;
  declarationToken: string;
  supplierContacts?: SupplierContact[];
  supplierName?: string | null;
  onApprove: (callbacks: ProductionDeclarationActionCallbacks) => void;
  onRefuse: (callbacks: ProductionDeclarationActionCallbacks) => void;
};

export function useProductionDeclarationEmailDialog({
  productionDeclarationId,
  purchaseOrderId,
  purchaseOrderName,
  declarationToken,
  supplierContacts,
  supplierName,
  onApprove,
  onRefuse,
}: UseProductionDeclarationEmailDialogParams) {
  const { t } = useTranslation('productionDeclaration');
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [currentEmailAction, setCurrentEmailAction] =
    useState<ProductionDeclarationEmailAction>('approve');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  const initialRecipientLabels = useMemo(() => {
    const labels: Record<string, string> = {};

    for (const contact of supplierContacts ?? []) {
      const email = contact.email?.trim();
      const name = contact.name?.trim();

      if (!email) {
        continue;
      }

      if (name) {
        labels[email] = name;
      }
    }

    return labels;
  }, [supplierContacts]);

  const initialRecipientEmails = useMemo(
    () =>
      (supplierContacts ?? [])
        .map((contact) => contact.email?.trim())
        .filter((email): email is string => Boolean(email)),
    [supplierContacts],
  );

  const supplierPortalLink = useMemo(() => {
    const origin = window.location.origin;
    const encodedToken = encodeURIComponent(declarationToken ?? '');

    return `${origin}/production-declarations/${productionDeclarationId}?token=${encodedToken}`;
  }, [declarationToken, productionDeclarationId]);

  const getDefaultMessage = useCallback(
    (action: ProductionDeclarationEmailAction) => {
      const resolvedSupplierName = supplierName?.trim()
        ? supplierName.trim()
        : t('sendEmailModalFallbackSupplierName');
      const poNumber = purchaseOrderName?.trim()
        ? purchaseOrderName.trim()
        : String(purchaseOrderId);

      return action === 'approve'
        ? getProductionDeclarationApproveDefaultMessage({
            poNumber,
            supplierName: resolvedSupplierName,
            supplierPortalLink,
          })
        : getProductionDeclarationRefuseDefaultMessage({
            poNumber,
            supplierName: resolvedSupplierName,
            supplierPortalLink,
          });
    },
    [purchaseOrderId, purchaseOrderName, supplierName, supplierPortalLink, t],
  );

  const dialogInitialized = useRef(false);

  const handleOpenEmailDialog = (action: ProductionDeclarationEmailAction) => {
    setCurrentEmailAction(action);
    setRecipients(initialRecipientEmails);
    setMessage(getDefaultMessage(action));
    setOpenEmailDialog(true);
    dialogInitialized.current = true;
  };

  useEffect(() => {
    if (!openEmailDialog) {
      dialogInitialized.current = false;
      return;
    }

    if (dialogInitialized.current) {
      return;
    }

    setRecipients(initialRecipientEmails);
    setMessage(getDefaultMessage(currentEmailAction));
    dialogInitialized.current = true;
  }, [
    openEmailDialog,
    initialRecipientEmails,
    currentEmailAction,
    getDefaultMessage,
  ]);

  const handleSendEmailDialog = () => {
    const onSuccess = () => setOpenEmailDialog(false);

    if (currentEmailAction === 'approve') {
      onApprove({ onSuccess, recipients, message });
      return;
    }

    onRefuse({ onSuccess, recipients, message });
  };

  return {
    openEmailDialog,
    setOpenEmailDialog,
    currentEmailAction,
    recipients,
    setRecipients,
    message,
    setMessage,
    initialRecipientLabels,
    handleOpenEmailDialog,
    handleSendEmailDialog,
  };
}
